import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { Link } from "react-router-dom";
import { Breadcrumb, Button, Form, Tooltip } from "antd";
import moment from "moment";
import debounce from "lodash/debounce";
import type {
  DestinationEditProps,
  DestinationEditFormProps,
  DestinationListSearchProps,
} from "../../store/slices/destinations/types";
import { DestinationView, DestinationEdit } from "../../components/DestinationDetails";
import { ViewModeType } from "../../lib/types";
import { checkNotLoaded } from "../../lib/utils";
import { useAppDispatch, useAppSelector } from "../..";
import {
  createDestination,
  fetchParentDestinationList,
  fetchDestinationSets,
  fetchDestinationById,
  fetchParentDestinationById,
  fetchDestinationVersions,
  cleanDestinationVersions,
  setParentDestinationList,
  cleanParentDestinationList,
  updateDestination,
} from "../../store/slices/destinations/destinations";
import {
  fetchCountries,
  fetchDestinationCategories,
  fetchDestinationTypes,
  fetchRegions,
} from "../../store/slices/dicts";
import { ReactComponent as EditIcon } from "../../img/versions_icons/editVersion_icon.svg";
import "./DestinationDetailsContainer.less";
import { hasAccess } from "../../components/RolesModule/hasAccess";
import { MENU_TC_ADMIN_DESTINATIONS_WRITE } from "../../lib/const/permissions";

export const DestinationDetailsContainer = () => {
  const params = useParams<{ destinationId: string }>();
  const { pathname } = useLocation();
  const destinationId = Number(params.destinationId);
  const [viewMode, setViewMode] = useState<ViewModeType>("view");
  const dispatch = useAppDispatch();
  const history = useHistory();
  const {
    countriesLoading,
    destinationTypesLoading,
    destinationCategoriesLoading,
    regionsLoading,
  } = useAppSelector((state) => state.dicts);
  const { destinationSetsLoading, destination, parentDestination } = useAppSelector(
    (state) => state.destinations
  );
  

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [form] = Form.useForm<DestinationEditFormProps>();

  useEffect(() => {
    if (pathname === "/destination/create") {
      setViewMode("create");
    } else {
      setViewMode("view");
    }
  }, [destinationId]);

  // Загрузка направления
  useEffect(() => {
    if (viewMode === "view" && destinationId > 0) {
      dispatch(fetchDestinationById(destinationId)).then(({ payload }) => {
        const { parentDestinationId } = payload;
        if (parentDestinationId) {
          dispatch(fetchParentDestinationById(parentDestinationId));
        }
      });
    }
  }, [destinationId, viewMode]);

  // Загрузка справочников
  useEffect(() => {
    if (checkNotLoaded(destinationSetsLoading)) dispatch(fetchDestinationSets());
    if (checkNotLoaded(destinationTypesLoading)) dispatch(fetchDestinationTypes());
    if (checkNotLoaded(destinationCategoriesLoading)) dispatch(fetchDestinationCategories());
    if (checkNotLoaded(countriesLoading)) dispatch(fetchCountries());
    if (checkNotLoaded(regionsLoading)) dispatch(fetchRegions());
  }, []);

  const getParentDestinationList = (searchValues: Partial<DestinationListSearchProps>): void => {
    const values: DestinationListSearchProps = {
      ...searchValues,
      page: 0,
      size: 30,
      onDate: moment(),
    };
    dispatch(fetchParentDestinationList(values));
  };

  const getParentDestinationListByName = debounce(
    (name: string) => getParentDestinationList({ name }),
    500
  );

  const handleCleanDestinationList = () => {
    dispatch(cleanParentDestinationList());
  };

  // Очистка списка направлений по выходу
  useEffect(() => handleCleanDestinationList, []);

  const onOpenVersions = () => {
    if (destination) {
      dispatch(fetchDestinationVersions(destination.id));
    }
  };

  const onCloseVersions = () => {
    dispatch(cleanDestinationVersions());
  };

  // Создание направления
  const handleCreateDestination = (params: DestinationEditProps) => {
    setIsLoading(true);
    dispatch(createDestination(params))
      .then(({ payload }) => {
        setIsLoading(false);
        history.push(`/destination/:${payload.id}`);
      })
      .finally(() => setIsLoading(false));
  };

  // Обновление направления
  const handleUpdateDestination = (params: DestinationEditProps) => {
    setIsLoading(true);
    dispatch(updateDestination({ ...params, id: destinationId }))
      .then(() => {
        setIsLoading(false);
        setViewMode("view");
      })
      .finally(() => setIsLoading(false));
  };

  const onSubmitEditing = (params: DestinationEditProps) => {
    switch (viewMode) {
      case "create":
        handleCreateDestination(params);
        break;
      case "edit":
        handleUpdateDestination(params);
      default:
        break;
    }
  };

  // Активация режима редактирования
  const setEditMode = () => {
    if (destination) {
      setViewMode("edit");
      // Название родительского направления берется из списка, поэтому существующее кладется в список
      if (parentDestination) {
        dispatch(setParentDestinationList([parentDestination]));
      }
    }
  };

  // Фунционал кнопки "Отмена" в режимах создания и редактирования
  const onCancelEdit = () => {
    switch (viewMode) {
      case "create":
        history.push("/destinations");
        break;
      case "edit":
        setViewMode("view");
        break;
      default:
        break;
    }
  };

  const title = viewMode === "create" ? "Создание направления" : "Детали направления";

  return (
    <div className="container_height destination-details-container">
      <Breadcrumb className="breadcrumb">
        <Breadcrumb.Item>
          <Link to="/destinations">Направления</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{title}</Breadcrumb.Item>
      </Breadcrumb>
      <h1 className="page-title">
        {title}
        {hasAccess(MENU_TC_ADMIN_DESTINATIONS_WRITE) && viewMode === "view" && (
          <>
            <Tooltip title="Редактировать направление">
              <Button
                className="button-icon destination-details-container__editButton"
                icon={<EditIcon />}
                type="text"
                htmlType="button"
                onClick={setEditMode}
              />
            </Tooltip>
          </>
        )}
      </h1>
      {(viewMode === "create" || viewMode === "edit") && (
        <DestinationEdit
          form={form}
          viewMode={viewMode}
          onCancelEdit={onCancelEdit}
          getParentDestinationListByName={getParentDestinationListByName}
          handleCleanDestinationList={handleCleanDestinationList}
          onSubmitEditing={onSubmitEditing}
          isLoading={isLoading}
        />
      )}
      {viewMode === "view" && (
        <DestinationView onOpenVersions={onOpenVersions} onCloseVersions={onCloseVersions} />
      )}
    </div>
  );
};
